import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { OrgCountBarChart } from "components/Charts/OrgCountBarChart";
import { Modal } from "components/Modal";
import {
  describeStrugglingFlag,
  LearnerStrugglingSummary,
  STRUGGLING_REASON,
} from "helpers/strugglingLearner";
import { FC, useEffect, useState } from "react";
import {
  fetchStrugglingLearnerComparison,
  RequirementRateComparison,
} from "services/strugglingLearners";

type UnderperformingDetailModalProps = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  summary: LearnerStrugglingSummary;
};

export const UnderperformingDetailModal: FC<UnderperformingDetailModalProps> = ({
  open,
  onClose,
  orgId,
  summary,
}) => {
  const theme = useTheme();
  const [requirements, setRequirements] = useState<RequirementRateComparison[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reasons = (summary.flags || [])
    .filter((flag) => flag.reasonCode !== STRUGGLING_REASON.THIN_DATA)
    .map(describeStrugglingFlag);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchStrugglingLearnerComparison(orgId, summary.studentId)
      .then((rows) => {
        if (!cancelled) setRequirements(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setRequirements([]);
          setError("Could not load the requirement comparison.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orgId, summary.studentId]);

  const hasCohort = requirements.some((row) => row.cohortRate != null);

  return (
    <Modal open={open} onClose={onClose} title="Underperforming" size="large">
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Why this learner is underperforming
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
            {reasons.map((reason) => (
              <Typography key={reason} component="li" variant="body2">
                {reason}
              </Typography>
            ))}
          </Stack>
        </Box>
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Ratings by requirement
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Average coach rating for this learner
            {hasCohort ? ", compared with other learners in the organization" : ""}.
          </Typography>
          {loading && (
            <Box py={4} display="flex" justifyContent="center">
              <CircularProgress size={28} />
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {!loading && !error && requirements.length === 0 && (
            <Alert severity="info">
              No completed ratings to compare yet.
            </Alert>
          )}
          {!loading && !error && requirements.length > 0 && (
            <OrgCountBarChart
              valueLabel="Average rating"
              data={requirements.map((row) => ({
                org: row.name,
                count: row.learnerRate,
                learner: row.learnerRate,
                ...(row.cohortRate != null ? { cohort: row.cohortRate } : {}),
              }))}
              series={[
                {
                  key: "learner",
                  label: "This learner",
                  color: theme.palette.warning.main,
                },
                ...(hasCohort
                  ? [
                      {
                        key: "cohort",
                        label: "Other learners",
                        color: theme.palette.primary.main,
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </Box>
      </Stack>
    </Modal>
  );
};
